/* Time is always Khao Lak time (Asia/Bangkok, GMT+7 — no DST, so the offset
   never moves).
   A guest still on New York time who opens the schedule and sees "Thursday"
   because their phone says so has been actively misled, so nothing here ever
   uses the device timezone — every format goes through Asia/Bangkok. */

export const TZ = 'Asia/Bangkok';

/* The wedding is in the future, which makes "next event" and "happening now"
   impossible to see while building. `?at=2026-11-06T10:30` pins the clock for a
   session so those states can be looked at; without it this is just Date.now(). */
const OVERRIDE_KEY = 'mv.clock';

function readOverride(): number | null {
  if (typeof window === 'undefined') return null;
  const fromUrl = new URLSearchParams(window.location.search).get('at');
  if (fromUrl) {
    const ms = Date.parse(fromUrl.includes('T') && !/[Z+]/.test(fromUrl) ? `${fromUrl}:00+07:00` : fromUrl);
    if (!Number.isNaN(ms)) {
      sessionStorage.setItem(OVERRIDE_KEY, String(ms));
      return ms;
    }
  }
  const stored = sessionStorage.getItem(OVERRIDE_KEY);
  return stored ? Number(stored) : null;
}

let override = readOverride();
let overrideSetAt = Date.now();

export function now(): number {
  if (override === null) return Date.now();
  // Let a pinned clock run forwards, so countdowns still tick.
  return override + (Date.now() - overrideSetAt);
}

export function isClockPinned(): boolean {
  return override !== null;
}

export function clearPinnedClock(): void {
  sessionStorage.removeItem(OVERRIDE_KEY);
  override = null;
  overrideSetAt = Date.now();
}

const timeFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: false });
const dayFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'short' });
const dayLongFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long' });
const keyFmt = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' });

export const fmtTime = (iso: string | number) => timeFmt.format(new Date(iso));
export const fmtDay = (iso: string | number) => dayFmt.format(new Date(iso));
export const fmtDayLong = (iso: string | number) => dayLongFmt.format(new Date(iso));
export const dayKey = (iso: string | number) => keyFmt.format(new Date(iso));

/** "in 2 hours" · "in 25 minutes" · "starting now" · null once it has started. */
export function untilLabel(startIso: string, at = now()): string | null {
  const diff = Date.parse(startIso) - at;
  if (diff <= 0) return null;
  const mins = Math.round(diff / 60000);
  if (mins <= 1) return 'starting now';
  if (mins < 60) return `in ${mins} minutes`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `in ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  const days = Math.round(hours / 24);
  return `in ${days} ${days === 1 ? 'day' : 'days'}`;
}

export function durationLabel(startIso: string, endIso?: string): string | null {
  if (!endIso) return null;
  const mins = Math.round((Date.parse(endIso) - Date.parse(startIso)) / 60000);
  if (mins < 60) return `${mins} minutes`;
  const hours = mins / 60;
  const rounded = Math.round(hours * 2) / 2;
  return `${rounded} ${rounded === 1 ? 'hour' : 'hours'}`;
}

/** How old a cached payload is, said plainly: "from 19:12". */
export function cacheAgeLabel(fetchedAt: number): string {
  return `from ${fmtTime(fetchedAt)}`;
}

export function relativeUploadTime(ms: number, at = now()): string {
  const diff = at - ms;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (dayKey(ms) === dayKey(at)) return `today, ${fmtTime(ms)}`;
  return `${fmtDay(ms)}, ${fmtTime(ms)}`;
}
